import classNames from 'classnames';
import * as React from 'react';
import { CurrentUserDto } from '../../../../../../types/UserDtos';
import { logout } from '../../../../../api/custom/UserLogout';
import * as ROUTES from '../../../../../routes';
import { getAdminRouteForNavigation } from '../../../../../routes-pairs';
import Avatar from '../../../../components/Avatar';
import { Divider, Dropdown, Option, OptionLink, OptionWithConfirm } from '../../../../components/Dropdown';
import { isFeatureEnabled } from '../../../../services/Features';
import replacement from '../../../../services/Replacement';
import { goToUrl } from '../../../../services/Router';
import { interpolateAppUrl, prependAppPath } from '../../../../services/Url';
import { isAnyAdmin } from '../../../../services/User';
import styles from './style.m.less';

type Props = {
    user: CurrentUserDto;
};

const UserMenu: React.FC<Props> = ({ user }) => {
    return (
        <Dropdown
            kind="PLAIN"
            showChevron={false}
            ariaLabel={`You're logged in as ${user.firstName}. Click to open user settings menu.`}
            label={
                isFeatureEnabled('FEATURE_layoutRefresh') ? (
                    <UserButton user={user} />
                ) : (
                    <div className="app-header-button-content">
                        <Avatar user={user} size="MD" />
                        <div className="app-header-button-label app-header-item-label">
                            {user.firstName} {user.impersonator && <>[{user.impersonator.firstName}]</>}
                        </div>
                    </div>
                )
            }
            eventTracking={{ category: 'navigation user menu', action: 'toggle' }}
            large={true}
            className={classNames({
                'top-aligned left-aligned': isFeatureEnabled('FEATURE_layoutRefresh'),
                [styles.userDropdownButtonContainer]: isFeatureEnabled('FEATURE_layoutRefresh'),
            })}
            buttonClass={isFeatureEnabled('FEATURE_layoutRefresh') && styles.userDropdownButton}
        >
            {!user.isGuest && (
                <>
                    <OptionLink
                        href={prependAppPath(ROUTES.USER_PROFILE_SHORT)}
                        eventTracking={{ category: 'navigation user menu', action: 'click', label: 'view profile' }}
                    >
                        View Profile
                    </OptionLink>
                    <OptionLink
                        href={interpolateAppUrl(ROUTES.USER_PROFILE, { id: user.id }) + '?edit'}
                        eventTracking={{ category: 'navigation user menu', action: 'click', label: 'edit profile' }}
                    >
                        Edit Profile
                    </OptionLink>
                    <OptionLink
                        href={prependAppPath(ROUTES.USER_SETTINGS)}
                        eventTracking={{ category: 'navigation user menu', action: 'click', label: 'your settings' }}
                    >
                        Your Settings
                    </OptionLink>
                </>
            )}
            <OptionWithConfirm
                onClick={() => goToUrl(`/changePasswordInternal/id/${user.id}`)}
                title="Change your password?"
                body="Your company is using single sign-on, so most likely you don't need a specific Small Improvements password. Proceed anyway?"
                confirmLabel="Proceed"
                isConfirmationActive={user.company.companySettings.ssoActive}
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'change password' }}
            >
                Change Password
            </OptionWithConfirm>
            <OptionLink
                href="/backups"
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'backups' }}
            >
                Backups
            </OptionLink>
            {!user.isGuest && (
                <OptionLink
                    href="/app/downloads"
                    eventTracking={{ category: 'navigation user menu', action: 'click', label: 'exports' }}
                >
                    Exports
                </OptionLink>
            )}
            {!user.isGuest && !isAnyAdmin(user) && (
                <OptionLink
                    href={prependAppPath(getAdminRouteForNavigation(ROUTES.YOUR_COMPANY_USERS))}
                    eventTracking={{ category: 'navigation user menu', action: 'click', label: 'company directory' }}
                >
                    {replacement('company.big')} Directory
                </OptionLink>
            )}
            <Option
                onClick={() => logout().then(() => goToUrl('/loggedout'))}
                eventTracking={{ category: 'navigation user menu', action: 'click', label: 'logout' }}
            >
                Log Out
            </Option>
            {user.isSmallImprovements && (
                <>
                    <Divider />
                    <OptionLink href="/app/observer/companies">Company Observer</OptionLink>
                    <OptionLink href="/app/observer/users">User Observer</OptionLink>
                    <OptionLink href="/app/observer/experiments">Experiment Observer</OptionLink>
                    <OptionLink href="/app/observer/users-to-emails">Users to Emails Tool</OptionLink>
                    <OptionLink href="/app/observer/features">Bulk Changes</OptionLink>
                    <OptionLink href="/app/observer/customer-health">Customer Health</OptionLink>
                    <OptionLink href="/app/system-access-roles">System Access Roles</OptionLink>
                    <OptionLink href="/paymentObserver">Payment Observer</OptionLink>
                </>
            )}
        </Dropdown>
    );
};

function UserButton({ user }: { user: CurrentUserDto }) {
    return (
        <>
            <Avatar user={user} size="MD" />
            <div className={styles.userDropdownButtonLabel}>
                {user.name} {user.impersonator && <>[{user.impersonator.firstName}]</>}
            </div>
        </>
    );
}

export default UserMenu;
